import React from 'react';
import { of } from 'rxjs';
import { List } from 'immutable';
import { complement } from 'ramda';
import { collateTPPs } from './tpps';
import { updateConsent } from './consents';
import { HistoryRecord } from './audit-history';
import { formatDate, durationRemaining } from '../lib/date-utils';
var CONSENT_STATUS_TO_HISTORY_LABEL = {
  ACCEPTED: 'Authorisation requested',
  AUTHORIZED: 'Connected since',
  DECLINED: 'Rejected',
  DELETED: 'Deleted',
  BLOCKED: 'Request blocked by bank',
  WITHDRAWN: 'Cancelled'
  // this is whatever MMonks gives us, and nothing else.
};
export var isActive = function isActive(fundsCheck) {
  return fundsCheck.get('grouping') === 'ACTIVE';
};
var isActiveTpp = function isActiveTpp(tpp) {
  return tpp.get('consents').some(isActive);
};
var updateFundsCheckGeneratedState = function updateFundsCheckGeneratedState(state) {
  var fundsChecks = List(state.getIn(['fundsChecks', 'index']).values());
  var tpps = collateTPPs(fundsChecks);
  var activeTpps = tpps.filter(isActiveTpp);
  var inactiveTpps = tpps.filter(complement(isActiveTpp));
  return state.setIn(['fundsChecks', 'tpps'], tpps).setIn(['fundsChecks', 'activeTpps'], activeTpps).setIn(['fundsChecks', 'inactiveTpps'], inactiveTpps);
};
export var updateFundsCheckGeneratedState$ = of(updateFundsCheckGeneratedState);
export function updateFundsCheckStateAfterLoad(updatedFundsCheck) {
  return function (state) {
    return state.updateIn(['fundsChecks', 'index', updatedFundsCheck.get('id')], function (fundsCheck) {
      return updateConsent(fundsCheck, updatedFundsCheck);
    }).update(updateFundsCheckGeneratedState);
  };
}
export function fundsCheckDescriptiveDate(fundsCheck) {
  var _fundsCheck$toJS = fundsCheck.toJS(),
    modifiedAt = _fundsCheck$toJS.modifiedAt,
    expiresAt = _fundsCheck$toJS.expiresAt,
    grouping = _fundsCheck$toJS.grouping;
  switch (grouping) {
    case 'DELETED':
      return {
        label: 'Deleted',
        date: modifiedAt,
        field: 'modifiedAt',
        getDescriptionText: function getDescriptionText(clientName) {
          return "Funds check permission is no longer given to ".concat(clientName, " as they requested to delete your connection.");
        }
      };
    case 'WITHDRAWN':
      return {
        label: 'Cancelled',
        date: modifiedAt,
        field: 'modifiedAt',
        getDescriptionText: function getDescriptionText(clientName) {
          return "Funds check permission is no longer given to ".concat(clientName, ". The permission was cancelled by either you or a member of staff.");
        }
      };
    case 'EXPIRED':
      return {
        label: 'Expired',
        date: expiresAt,
        field: 'expiresAt',
        getDescriptionText: function getDescriptionText(clientName) {
          return "Funds check permission is no longer given to ".concat(clientName, ". The permission you gave on ").concat(formatDate(expiresAt, 'Do MMMM YYYY'), " has expired.");
        }
      };
    case 'DECLINED':
    case 'BLOCKED':
      return {
        label: 'Rejected',
        date: modifiedAt,
        field: 'modifiedAt',
        getDescriptionText: function getDescriptionText(clientName) {
          return "Funds check permission is not available for ".concat(clientName, ".  The customer declined to authorise this consent.");
        }
      };
    case 'TPP_REVOKED':
      return {
        label: 'Revoked',
        date: '',
        field: '',
        getDescriptionText: function getDescriptionText(clientName) {
          return "Funds check permission is no longer given to ".concat(clientName, " as it's been revoked. Please contact them for more information.");
        }
      };
    case 'TPP_CLIENT_SUSPENDED':
    case 'TPP_SUSPENDED':
      return {
        label: 'Suspended',
        date: '',
        field: '',
        getDescriptionText: function getDescriptionText(clientName) {
          return "Funds check permission is no longer given to ".concat(clientName, ". Please contact them for more information.");
        }
      };
    default:
      return expiresAt ? {
        label: 'Expires',
        date: expiresAt,
        field: 'expiresAt',
        getDescriptionText: function getDescriptionText(clientName) {
          var accessRemainingTime = durationRemaining(expiresAt);
          return "On ".concat(formatDate(modifiedAt, 'Do MMMM YYYY'), ", you gave funds check permission to ").concat(clientName, ". The permission expires in ").concat(accessRemainingTime, ". ");
        }
      } : {
        label: 'Ongoing',
        date: '',
        field: 'expiresAt',
        getDescriptionText: function getDescriptionText(clientName) {
          return "On ".concat(formatDate(modifiedAt, 'Do MMMM YYYY'), ", you gave funds check permission to ").concat(clientName, ".");
        }
      };
  }
}
function toHistoryRecords(consentAudit) {
  return new HistoryRecord({
    date: consentAudit.changeAt,
    status: consentAudit.status,
    label: CONSENT_STATUS_TO_HISTORY_LABEL[consentAudit.status] || '-'
  });
}
function getGroupingHistory(fundsCheck) {
  var groupingHistory = List();
  switch (fundsCheck.get('grouping')) {
    case 'TPP_REVOKED':
    case 'REVOKED':
      return groupingHistory.push(new HistoryRecord({
        date: null,
        label: 'Revoked'
      }));
    case 'SUSPENDED':
    case 'TPP_CLIENT_SUSPENDED':
    case 'TPP_SUSPENDED':
      return groupingHistory.push(new HistoryRecord({
        date: null,
        label: 'Suspended'
      }));
    case 'EXPIRED':
      return groupingHistory.push(new HistoryRecord({
        date: fundsCheck.get('expiresAt'),
        label: 'Expired'
      }));
    default:
      return groupingHistory;
  }
}
var getHistory = function getHistory(fundsCheck, consentAudits, activities) {
  var activityHistory = activities.map(function (activity) {
    return new HistoryRecord({
      date: new Date(activity.get('modifiedAt')),
      status: activity.get('status'),
      reference: activity.get('reference'),
      amount: activity.getIn(['payload', 'amountString']) || Number(activity.getIn(['payload', 'amount'])),
      currency: activity.getIn(['payload', 'currency']),
      isAmountAvailable: activity.getIn(['payload', 'isAmountAvailable']),
      label: '-'
    });
  });
  return consentAudits.map(toHistoryRecords).concat(activityHistory).sortBy(function (record) {
    return record.get('date');
  }).concat(getGroupingHistory(fundsCheck)).reverse();
};
export function processFundsCheckConsents(consents) {
  return consents.map(function (consent) {
    var innerPayload = JSON.parse(consent.getIn(['payload', 'requestPayload', 'payload'], '{}'));
    // Currently the 'innerPayload.ExpirationDateTime' best reflects the true expiration date of the funds check
    var actualExpiresAt = innerPayload.Data && innerPayload.Data.ExpirationDateTime;
    return consent.set('expiresAt', actualExpiresAt ? new Date(actualExpiresAt) : null);
  });
}
export function updateFundsCheckHistoryState(fundsCheckId, consentAudits, activities) {
  return function (state) {
    return state.updateIn(['fundsChecks', 'index', fundsCheckId], function (fundsCheck) {
      return fundsCheck.set('history', getHistory(fundsCheck, consentAudits, activities));
    }).update(updateFundsCheckGeneratedState);
  };
}
export function updateFundsCheckActivityState(fundsCheckId, activities) {
  return function (state) {
    return state.updateIn(['fundsChecks', 'index', fundsCheckId], function (fundsCheck) {
      if (activities.size > 0 && fundsCheck.has('activity')) {
        var changeAt = activities.reverse().getIn([0, 'modifiedAt']);
        return fundsCheck.setIn(['activity', 'changeAt'], changeAt);
      }
      return fundsCheck;
    }).update(updateFundsCheckGeneratedState);
  };
}
export function getHistoryForCurrentUser(fundsCheck) {
  var isStaffMember = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return isStaffMember ? fundsCheck.history : fundsCheck.history.filter(function (_ref) {
    var status = _ref.status;
    return status !== 'ACCEPTED';
  });
}